import axios from 'axios';
import { AuthService } from './auth.service';
import { ENDPOINTS } from '../constants/endpoints';
import { IInstitutionSettings, WeekDay } from '../interfaces/institutionSettings.interface';

interface BackendSettings {
    id?: string;
    institutionId: string;
    isAttendanceButtonEnabled: boolean;
    weekendDays?: string;
    twoFactorPolicy?: 'OPTIONAL' | 'REQUIRED';
    attendanceLocationMode?: string;
    locationAccuracyThresholdMeters?: number;
}

const DEFAULT_WEEKEND: WeekDay[] = ['FRIDAY', 'SATURDAY'];

/** Bangladesh default, used by display screens when the weekend cannot be loaded. */
export const FALLBACK_WEEKEND: WeekDay[] = ['FRIDAY'];

const toFrontend = (data: BackendSettings): IInstitutionSettings => ({
    id: data.id,
    institutionId: data.institutionId,
    isAttendanceButtonEnabled: data.isAttendanceButtonEnabled,
    weekendDays: data.weekendDays
        ? (data.weekendDays.split(',').map(s => s.trim()).filter(Boolean) as WeekDay[])
        : DEFAULT_WEEKEND,
    twoFactorPolicy: data.twoFactorPolicy,
    attendanceLocationMode: data.attendanceLocationMode === 'FLAG' ? 'FLAG' : 'OFF',
    locationAccuracyThresholdMeters: data.locationAccuracyThresholdMeters ?? 100,
});

const toBackend = (data: Partial<IInstitutionSettings>): Partial<BackendSettings> => {
    const payload: Partial<BackendSettings> = {};
    if (data.isAttendanceButtonEnabled !== undefined) {
        payload.isAttendanceButtonEnabled = data.isAttendanceButtonEnabled;
    }
    if (data.weekendDays !== undefined) {
        payload.weekendDays = data.weekendDays.join(',');
    }
    return payload;
};

export const InstitutionSettingsService = {
    getSettings: async (institutionId: string): Promise<IInstitutionSettings | null> => {
        const response = await axios.get<BackendSettings | null>(
            ENDPOINTS.INSTITUTION_SETTINGS.BY_INSTITUTION(institutionId),
            { headers: AuthService.getAuthHeader() }
        );
        return response.data ? toFrontend(response.data) : null;
    },

    /** Read-only weekend days; allowed for any member of the institution (no settings permission needed). */
    getWeekendDays: async (institutionId: string): Promise<WeekDay[]> => {
        const response = await axios.get<{ weekendDays?: string }>(
            ENDPOINTS.INSTITUTION_SETTINGS.WEEKEND_DAYS(institutionId),
            { headers: AuthService.getAuthHeader() }
        );
        return (response.data?.weekendDays ?? '').split(',').map(s => s.trim()).filter(Boolean) as WeekDay[];
    },

    updateSettings: async (institutionId: string, data: Partial<IInstitutionSettings>): Promise<IInstitutionSettings> => {
        const response = await axios.put<BackendSettings>(
            ENDPOINTS.INSTITUTION_SETTINGS.BY_INSTITUTION(institutionId),
            toBackend(data),
            { headers: AuthService.getAuthHeader() }
        );
        return toFrontend(response.data);
    },

    updateWeekendDays: async (institutionId: string, weekendDays: WeekDay[]): Promise<IInstitutionSettings> => {
        const response = await axios.put<BackendSettings>(
            ENDPOINTS.INSTITUTION_SETTINGS.WEEKEND_DAYS(institutionId),
            { weekendDays: weekendDays.join(',') },
            { headers: AuthService.getAuthHeader() }
        );
        return toFrontend(response.data);
    },

    updateTwoFactorPolicy: async (institutionId: string, policy: 'OPTIONAL' | 'REQUIRED'): Promise<void> => {
        await axios.put(ENDPOINTS.INSTITUTION_SETTINGS.TWO_FACTOR_POLICY(institutionId), { policy }, {
            headers: AuthService.getAuthHeader(),
        });
    },

    updateLocationPolicy: async (institutionId: string, mode: string, thresholdMeters?: number): Promise<void> => {
        await axios.put(ENDPOINTS.INSTITUTION_SETTINGS.LOCATION_POLICY(institutionId), null, {
            params: { mode, thresholdMeters },
            headers: AuthService.getAuthHeader(),
        });
    },
};
